import type { CustomTimeRange, RangePreset } from '@plato/shared-util/date-time-range';
import { normaliseTimeRange } from '@plato/shared-util/date-time-range';
import { SelectTriggerButton } from '@plato/ui-legacy/components/ui/select-trigger-button';
import { formatShortDate } from '@plato/ui-legacy/lib/date-helpers';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { useDateFnsLocale } from '@plato/ui-legacy/lib/hooks/use-date-fns-locale';
import { DateRangePicker, type DateRangePreset } from '@plato/ui/components/date-picker';
import { isSameDay } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

export type PresetDatePickerPreset = RangePreset | 'CUSTOM_DATES';

export type PresetDatePickerValue = {
    preset: PresetDatePickerPreset;
    /** Only meaningful for `CUSTOM_DATES` — every other preset computes its own range. */
    dateRange: CustomTimeRange | null;
};

export type PresetDatePickerProps = {
    value: PresetDatePickerValue;
    onChange: (value: PresetDatePickerValue) => void;
};

/** Every window that computes its own range — `CUSTOM_DATES` is the calendar itself. */
const computedWindows = [
    'TODAY',
    'YESTERDAY',
    'LAST_WEEK',
    'LAST_MONTH',
    'LAST_QUARTER',
    'TTM',
    'FULL_YEAR',
    'YTD',
] as const satisfies readonly RangePreset[];

/** A committed range read back as a preset, so "the last 7 days" keeps its name. */
function matchPreset(range: CustomTimeRange): PresetDatePickerPreset {
    const matched = computedWindows.find((preset) => {
        const { from, to } = normaliseTimeRange({ preset });
        return isSameDay(from, range.from) && isSameDay(to, range.to);
    });

    return matched ?? 'CUSTOM_DATES';
}

/**
 * The timeframe filter: preset windows plus a custom range, committed together.
 * The trigger names the preset rather than spelling out its dates, so only a
 * custom range shows as `from – to`.
 */
export function PresetDatePicker({ value, onChange }: PresetDatePickerProps): ReactElement {
    const { t } = useTranslation('preset-date-picker');
    const locale = useResolvedLocale();
    const dateFnsLocalization = useDateFnsLocale(locale);
    const [open, setOpen] = useState(false);

    const presetLabel = (preset: PresetDatePickerPreset): string => {
        switch (preset) {
            case 'TODAY':
                return t('option__today');
            case 'YESTERDAY':
                return t('option__yesterday');
            case 'LAST_WEEK':
                return t('option__last_week');
            case 'LAST_MONTH':
                return t('option__last_month');
            case 'LAST_QUARTER':
                return t('option__last_quarter');
            case 'TTM':
                return t('option__ttm');
            case 'FULL_YEAR':
                return t('option__full_year');
            case 'YTD':
                return t('option__ytd');
            case 'CUSTOM_DATES':
                return t('option__custom');
        }
    };

    // Yesterday is matchable but never offered, as in the picker this replaces.
    const presets: DateRangePreset[] = computedWindows
        .filter((preset) => preset !== 'YESTERDAY')
        .map((preset) => ({ label: presetLabel(preset), getValue: () => normaliseTimeRange({ preset }) }));

    const isCustom = value.preset === 'CUSTOM_DATES';
    const triggerText = isCustom
        ? value.dateRange &&
          `${formatShortDate(value.dateRange.from, locale)} – ${formatShortDate(value.dateRange.to, locale)}`
        : presetLabel(value.preset);

    return (
        <DateRangePicker
            title={t('picker__title')}
            placeholder={t('option__custom')}
            presets={presets}
            customDatesLabel={t('option__custom')}
            applyLabel={t('apply', { ns: 'common' })}
            cancelLabel={t('form__cancel', { ns: 'common' })}
            locale={dateFnsLocalization}
            open={open}
            onOpenChange={setOpen}
            value={
                value.preset === 'CUSTOM_DATES'
                    ? (value.dateRange ?? undefined)
                    : normaliseTimeRange({ preset: value.preset })
            }
            onValueChange={(range) => {
                if (!range?.from || !range.to) {
                    return;
                }

                const committed = { from: range.from, to: range.to };
                const preset = matchPreset(committed);
                onChange({
                    preset,
                    dateRange: preset === 'CUSTOM_DATES' ? committed : null,
                });
            }}
            render={
                <SelectTriggerButton>
                    <div className="flex gap-2 items-center">
                        <CalendarIcon className="h-4 w-4" />
                        <span className="text-sm text-neutral-900">{triggerText ?? t('option__custom')}</span>
                    </div>
                </SelectTriggerButton>
            }
        />
    );
}
